'use client';

import { useMemo, useEffect, useCallback, useRef } from 'react';
import {
  ReactFlow,
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  useReactFlow,
  useNodesInitialized,
  type Node,
  type ColorMode,
  type Edge,
  MarkerType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { InternalNetworkAdEntityKindSchema } from '@terra/shared-schemas-orchestrator';
import type { AdGraphResponseDto } from '@terra/shared-contracts-internal-network';
import { useThemeMode } from '@terra/ui-design-system';
import { Box } from '@terra/ui-styled-system';
import {
  type InternalNetworkAdEntityKind,
  type InternalNetworkGraphLayout,
  type InternalNetworkGraphLayoutPreset,
  type InternalNetworkNodeDensity,
  GRAPH_EDGE_COLOR,
  GRAPH_NODE_COLOR,
  type InternalNetworkNodeData,
} from './constants';
import {
  buildInternalNetworkFlowElements,
  internalNetworkEdgeTypes,
  internalNetworkNodeTypes,
  type InternalNetworkGraphViewScope,
} from './InternalNetworkGraphShared';
import {
  GRAPH_COLORS,
  shouldPreserveNodePositions,
  useAutoLayout,
  type AutoLayoutConfig,
  type HierarchyRankDirection,
} from '../flow-layout';
import { InternalNetworkGraphControls } from './InternalNetworkGraphControls';
import { InternalNetworkGraphNodeActionsProvider } from './InternalNetworkGraphNodeActionsContext';

const AUTO_LAYOUT_BY_GRAPH_LAYOUT: Record<
  InternalNetworkGraphLayout,
  NonNullable<AutoLayoutConfig['layout']>
> = {
  standard: 'forceAtlas2',
  radial: 'radial',
  sequential: 'hierarchy',
  horizontal: 'hierarchy',
};

const RANK_DIRECTION_BY_GRAPH_LAYOUT: Record<
  InternalNetworkGraphLayout,
  HierarchyRankDirection
> = {
  standard: 'LR',
  radial: 'LR',
  sequential: 'TB',
  horizontal: 'LR',
};

type GraphLayoutSpacing = {
  nodesep: number;
  ranksep: number;
  fitViewPadding: number;
  fitViewMaxZoom?: number;
};

const DRAWER_PATH_LAYOUT_SPACING: GraphLayoutSpacing = {
  nodesep: 56,
  ranksep: 100,
  fitViewPadding: 0.2,
  fitViewMaxZoom: 1.3,
};

const MAIN_LAYOUT_SPACING_BY_GRAPH_LAYOUT: Record<
  InternalNetworkGraphLayout,
  GraphLayoutSpacing
> = {
  standard: { nodesep: 320, ranksep: 600, fitViewPadding: 0.1 },
  radial: { nodesep: 220, ranksep: 600, fitViewPadding: 0.1 },
  sequential: {
    nodesep: 80,
    ranksep: 500,
    fitViewPadding: 0.1,
    fitViewMaxZoom: 1.2,
  },
  horizontal: { nodesep: 220, ranksep: 600, fitViewPadding: 0.1 },
};

type InternalNetworkGraphFlowProps = {
  graph: AdGraphResponseDto;
  layout: InternalNetworkGraphLayout;
  showNodeLabels: boolean;
  showEdgeLabels: boolean;
  focusedNodeId: string | null;
  selectedNodeId: string | null;
  selectedEdgeId: string | null;
  isExpanding: boolean;
  onSelectNode: (
    nodeId: string,
    entityKind: InternalNetworkAdEntityKind,
  ) => void;
  onExpandNode: (nodeId: string) => void;
  onViewDetails: (nodeId: string) => void;
  onSelectEdge: (edgeId: string | null) => void;
  onClearSelection: () => void;
  showMiniMap?: boolean;
  showControls?: boolean;
  onShowNodeLabelsChange?: (value: boolean) => void;
  onShowEdgeLabelsChange?: (value: boolean) => void;
  viewScope?: InternalNetworkGraphViewScope;
  centerNodeId?: string | null;
  onlyRenderVisibleElements?: boolean;
  layoutPreset?: InternalNetworkGraphLayoutPreset;
  nodeDensity?: InternalNetworkNodeDensity;
};

export function InternalNetworkGraphFlow({
  graph,
  layout,
  showNodeLabels,
  showEdgeLabels,
  focusedNodeId,
  selectedNodeId,
  selectedEdgeId,
  isExpanding,
  onSelectNode,
  onExpandNode,
  onViewDetails,
  onSelectEdge,
  onClearSelection,
  showMiniMap = true,
  showControls = true,
  onShowNodeLabelsChange,
  onShowEdgeLabelsChange,
  viewScope = 'full',
  centerNodeId = null,
  onlyRenderVisibleElements = graph.nodes.length > 500,
  layoutPreset = 'main',
  nodeDensity = 'default',
}: InternalNetworkGraphFlowProps) {
  const { mode: themeMode } = useThemeMode();
  const colorMode: ColorMode = themeMode === 'dark' ? 'dark' : 'light';
  const { setCenter } = useReactFlow();
  const nodesInitialized = useNodesInitialized();

  const { flowNodes, flowEdges } = useMemo(
    () =>
      buildInternalNetworkFlowElements({
        graph,
        showNodeLabels,
        showEdgeLabels,
        viewScope,
        centerNodeId,
        nodeDensity,
      }),
    [
      graph,
      showNodeLabels,
      showEdgeLabels,
      viewScope,
      centerNodeId,
      nodeDensity,
    ],
  );

  const layoutDependency = useMemo(
    () =>
      [
        flowNodes
          .map((node) => node.id)
          .sort()
          .join('\0'),
        flowEdges
          .map((edge) => `${edge.source}->${edge.target}`)
          .sort()
          .join('\0'),
        layout,
        layoutPreset,
      ].join('|'),
    [flowEdges, flowNodes, layout, layoutPreset],
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(flowNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(flowEdges);
  const lastCenteredNodeRef = useRef<string | null>(null);
  const focusedNodeIdRef = useRef<string | null>(focusedNodeId);
  const autoLayout = AUTO_LAYOUT_BY_GRAPH_LAYOUT[layout];

  useEffect(() => {
    setNodes((currentNodes) => {
      const positionsById = new Map(
        currentNodes.map((node) => [node.id, node.position]),
      );
      const currentIds = new Set(currentNodes.map((node) => node.id));
      const preservePositions = shouldPreserveNodePositions({
        currentNodeIds: currentIds,
        nextNodeIds: flowNodes.map((node) => node.id),
      });

      return flowNodes.map((node) => {
        const previousPosition = preservePositions
          ? positionsById.get(node.id)
          : undefined;
        const selected =
          focusedNodeIdRef.current === node.id || selectedNodeId === node.id;

        if (!previousPosition) {
          return {
            ...node,
            selected,
            data: {
              ...node.data,
              dimmed: false,
            },
          };
        }

        return {
          ...node,
          position: previousPosition,
          selected,
          data: {
            ...node.data,
            dimmed: false,
          },
        };
      });
    });
    setEdges(
      flowEdges.map((edge) => ({
        ...edge,
        selected: selectedEdgeId === edge.id,
        data: {
          ...(edge.data ?? {}),
          dimmed: selectedEdgeId !== null && selectedEdgeId !== edge.id,
        },
      })),
    );
  }, [
    flowNodes,
    flowEdges,
    selectedNodeId,
    selectedEdgeId,
    setNodes,
    setEdges,
  ]);

  useEffect(() => {
    const previousFocusedNodeId = focusedNodeIdRef.current;

    if (previousFocusedNodeId === focusedNodeId) {
      return;
    }

    focusedNodeIdRef.current = focusedNodeId;

    setNodes((currentNodes) =>
      currentNodes.map((node) => {
        if (
          node.id !== previousFocusedNodeId &&
          node.id !== focusedNodeId &&
          node.id !== selectedNodeId
        ) {
          return node;
        }

        const selected =
          focusedNodeId === node.id || selectedNodeId === node.id;

        if (node.selected === selected) {
          return node;
        }

        return {
          ...node,
          selected,
        };
      }),
    );
  }, [focusedNodeId, selectedNodeId, setNodes]);

  const layoutSpacing =
    layoutPreset === 'drawerPath'
      ? DRAWER_PATH_LAYOUT_SPACING
      : MAIN_LAYOUT_SPACING_BY_GRAPH_LAYOUT[layout];

  const { isLayoutPending } = useAutoLayout<InternalNetworkNodeData>({
    layout: autoLayout,
    rankdir: RANK_DIRECTION_BY_GRAPH_LAYOUT[layout],
    nodesep: layoutSpacing.nodesep,
    ranksep: layoutSpacing.ranksep,
    fitViewPadding: layoutSpacing.fitViewPadding,
    fitViewMaxZoom: layoutSpacing.fitViewMaxZoom,
    layoutDependency,
    preserveViewportOnGrowth: true,
  });

  useEffect(() => {
    if (!focusedNodeId) {
      lastCenteredNodeRef.current = null;

      return;
    }

    if (!nodesInitialized) {
      return;
    }

    const focusedNode = nodes.find((node) => node.id === focusedNodeId);

    if (!focusedNode) {
      return;
    }

    const { x, y } = focusedNode.position;
    const centerKey = `${focusedNodeId}:${x}:${y}`;

    if (lastCenteredNodeRef.current === centerKey) {
      return;
    }

    lastCenteredNodeRef.current = centerKey;
    const width = focusedNode.measured?.width ?? focusedNode.width ?? 160;
    const height = focusedNode.measured?.height ?? focusedNode.height ?? 108;

    void setCenter(x + width / 2, y + height / 2, { duration: 400, zoom: 1.3 });
  }, [focusedNodeId, nodes, nodesInitialized, setCenter]);

  const getNodeColor = useCallback((node: Node<InternalNetworkNodeData>) => {
    return GRAPH_NODE_COLOR[node.data.entityKind] ?? GRAPH_EDGE_COLOR;
  }, []);

  const parseNodeKind = useCallback((node: Node<InternalNetworkNodeData>) => {
    return InternalNetworkAdEntityKindSchema.safeParse(node.data.entityKind);
  }, []);

  const nodeActions = useMemo(
    () => ({
      selectedNodeId: selectedEdgeId ? null : selectedNodeId,
      isExpanding,
      onExpand: onExpandNode,
      onViewDetails,
      onCloseSelection: onClearSelection,
    }),
    [
      selectedEdgeId,
      selectedNodeId,
      isExpanding,
      onExpandNode,
      onViewDetails,
      onClearSelection,
    ],
  );

  return (
    <InternalNetworkGraphNodeActionsProvider value={nodeActions}>
      <Box position="relative" w="full" h="full">
        {showControls && onShowNodeLabelsChange && onShowEdgeLabelsChange ? (
          <Box position="absolute" top={3} right={3} zIndex={5}>
            <InternalNetworkGraphControls
              showNodeLabels={showNodeLabels}
              onShowNodeLabelsChange={onShowNodeLabelsChange}
              showEdgeLabels={showEdgeLabels}
              onShowEdgeLabelsChange={onShowEdgeLabelsChange}
            />
          </Box>
        ) : null}
        <ReactFlow<Node<InternalNetworkNodeData>, Edge>
          colorMode={colorMode}
          nodes={nodes}
          edges={edges}
          style={{
            width: '100%',
            height: '100%',
            visibility: isLayoutPending ? 'hidden' : 'visible',
          }}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={(_event, node) => {
            const parsedKind = parseNodeKind(node);

            if (!parsedKind.success) {
              return;
            }

            onSelectEdge(null);
            onSelectNode(node.id, parsedKind.data);
          }}
          onNodeDoubleClick={(_event, node) => {
            const parsedKind = parseNodeKind(node);

            if (!parsedKind.success) {
              return;
            }

            onExpandNode(node.id);
          }}
          onEdgeClick={(_event, edge: Edge) => {
            onSelectEdge(edge.id);
          }}
          onPaneClick={() => {
            onClearSelection();
          }}
          nodeTypes={internalNetworkNodeTypes}
          edgeTypes={internalNetworkEdgeTypes}
          minZoom={0.05}
          maxZoom={7}
          onlyRenderVisibleElements={onlyRenderVisibleElements}
          proOptions={{ hideAttribution: true }}
          attributionPosition="bottom-left"
          defaultEdgeOptions={{
            type: 'internalNetworkEdge',
            animated: false,
            markerEnd: {
              type: MarkerType.ArrowClosed,
              width: 18,
              height: 18,
            },
          }}
        >
          <Background
            variant={BackgroundVariant.Dots}
            color={GRAPH_COLORS.background}
            gap={12}
          />
          <Controls showInteractive={false} />
          {showMiniMap && flowNodes.length <= 500 ? (
            <MiniMap
              nodeStrokeWidth={0}
              nodeColor={getNodeColor}
              zoomable
              pannable
              position="bottom-right"
            />
          ) : null}
        </ReactFlow>
      </Box>
    </InternalNetworkGraphNodeActionsProvider>
  );
}
